<template>
  <v-menu v-model="isOpen" bottom left offset-y :close-on-content-click="false" @input="onMenuToggle">
    <template #activator="{ on: menuOn, attrs }">
      <v-tooltip bottom>
        <template #activator="{ on: tipOn }">
          <v-btn icon x-small tabindex="-1" v-bind="attrs" v-on="{ ...menuOn, ...tipOn }" @mousedown.prevent>
            <v-icon small>mdi-keyboard-space</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("components.utility.HtmlEditor.placeholderTooltip") }}</span>
      </v-tooltip>
    </template>

    <v-card>
      <v-text-field
        ref="search"
        v-model="search"
        autofocus
        dense
        hide-details
        outlined
        clearable
        class="ma-2"
        prepend-inner-icon="mdi-magnify"
        :label="$t('components.utility.HtmlEditor.placeholderSearch')"
      />
      <v-list dense max-height="320" class="overflow-y-auto py-0">
        <v-list-item v-for="item of filteredTokens" :key="item.token" @click="selectItem(item.token)">
          <v-list-item-content>
            <v-list-item-title>{{ item.value }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ tokenLabel(item.token) }} <span class="text--disabled ml-2">{{ item.token }}</span>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="!filteredTokens.length" disabled>
          <v-list-item-title>{{ $t("components.utility.HtmlEditor.placeholderNoResults") }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import Fuse from "fuse.js";
import { getAllTemplateTokens, getTemplateTokenLabel } from "@/lib/utility/template-token-catalog";

interface PlaceholderMenuItem {
  token: string;
  value: string;
  label: string;
}

/**
 * Searchable "insert placeholder" menu for the email-sending rich text editor. Lists only the
 * template-token-catalog paths that are actually present in the current context (i.e. resolve to a
 * simple, non-object value there), showing that value so the user can tell what they're inserting.
 */
@Component
export default class HtmlEditorPlaceholderMenu extends Vue {
  /** Resolves a token path to its value in the current context; tokens without a simple resolved value are hidden. */
  @Prop()
  resolveToken?: (token: string) => unknown;

  search = "";
  isOpen = false;

  get allTokens(): PlaceholderMenuItem[] {
    if (!this.resolveToken) return [];

    const items: PlaceholderMenuItem[] = [];
    for (const token of getAllTemplateTokens()) {
      const value = this.resolveToken(token);
      const isSimpleValue = typeof value === "string" || typeof value === "number";
      if (isSimpleValue && value !== "") {
        items.push({ token, value: String(value), label: getTemplateTokenLabel(token) });
      }
    }
    return items;
  }

  get fuse(): Fuse<PlaceholderMenuItem> {
    return new Fuse(this.allTokens, { threshold: 0.4, keys: ["token", "value", "label"] });
  }

  get filteredTokens(): PlaceholderMenuItem[] {
    if (!this.search) return this.allTokens;
    return this.fuse.search(this.search).map((result) => result.item);
  }

  tokenLabel(tokenPath: string): string {
    return getTemplateTokenLabel(tokenPath);
  }

  selectItem(token: string) {
    this.$emit("select", token);
    this.isOpen = false;
  }

  onMenuToggle(isOpen: boolean) {
    if (!isOpen) return;
    this.search = "";
    this.$nextTick(() => (this.$refs.search as Vue & { focus?: () => void })?.focus?.());
  }
}
</script>
